import React from 'react';
import { Box } from '@mui/material';
import { PRES, TYPE } from './chrome';
import { catalogFlagLabel, languageVersionCode } from './languageVersionHelpers';
import { COPY } from './presentationCopy';
import { PresentationFamilyMember, PresentationLanguage } from './types';
import { focusVisibleSx } from './ui/focus';

interface LanguageFlagsProps {
  members: PresentationFamilyMember[];
  onOpen: (id: string) => void;
}

function FlagMark({ language }: { language: PresentationLanguage | string }) {
  if (language === 'sv') {
    return (
      <svg width="14" height="10" viewBox="0 0 16 10" aria-hidden>
        <rect width="16" height="10" fill="#006AA7" />
        <rect x="5" width="2.2" height="10" fill="#FECC00" />
        <rect y="4" width="16" height="2.2" fill="#FECC00" />
      </svg>
    );
  }
  if (language === 'fi') {
    return (
      <svg width="14" height="10" viewBox="0 0 16 10" aria-hidden>
        <rect width="16" height="10" fill="#FFFFFF" />
        <rect x="0" y="0" width="16" height="10" fill="none" stroke="#d0d4dc" strokeWidth="0.8" />
        <rect x="4.6" width="2.4" height="10" fill="#003580" />
        <rect y="3.8" width="16" height="2.4" fill="#003580" />
      </svg>
    );
  }
  if (language === 'en') {
    return (
      <svg width="14" height="10" viewBox="0 0 16 10" aria-hidden>
        <rect width="16" height="10" fill={PRES.navy} />
        <text
          x="8"
          y="7.35"
          textAnchor="middle"
          fill={PRES.white}
          fontSize="6.2"
          fontWeight="700"
          fontFamily={TYPE.ui}
        >
          EN
        </text>
      </svg>
    );
  }
  const code = typeof language === 'string' && language !== 'sv' && language !== 'fi' && language !== 'en'
    ? language.toUpperCase().slice(0, 3)
    : languageVersionCode(language as PresentationLanguage);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 14,
        height: 10,
        px: 0.25,
        fontFamily: TYPE.ui,
        fontSize: 7,
        fontWeight: 700,
        letterSpacing: '0.02em',
        color: PRES.navy,
        lineHeight: 1,
      }}
    >
      {code}
    </Box>
  );
}

const LanguageFlags: React.FC<LanguageFlagsProps> = ({ members, onOpen }) => (
  <Box
    role="group"
    aria-label={COPY.languageVersions}
    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
  >
    {members.map((member) => {
      const label = catalogFlagLabel(member, members);
      return (
        <Box
          key={member._id}
          component="button"
          type="button"
          title={label}
          aria-label={label}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpen(member._id);
          }}
          onMouseDown={(event) => event.stopPropagation()}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 18,
            p: 0,
            border: `1px solid ${PRES.borderSubtle}`,
            borderRadius: '3px',
            bgcolor: PRES.white,
            cursor: 'pointer',
            ...focusVisibleSx,
            '&:hover': { borderColor: PRES.borderStrong, bgcolor: PRES.surfaceSunken },
          }}
        >
          <FlagMark language={member.language} />
        </Box>
      );
    })}
  </Box>
);

export default LanguageFlags;
